import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../lib/site";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/fleet", label: "Fleet" },
  { to: "/service-area", label: "Service Area" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const menu = open ? createPortal(
    <div
      data-testid="mobile-menu"
      className="lg:hidden fixed inset-0 z-[100] bg-black overflow-y-auto"
    >
      <div className="flex items-center justify-between h-[64px] px-4 border-b border-white/10">
        <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
          <img src={SITE.logo} alt="" className="h-10 w-10 object-contain" />
          <div className="leading-tight">
            <div className="font-display text-base">ALLIANCE</div>
            <div className="font-display text-[10px] text-[var(--ac-red)] tracking-[0.2em]">CRANE SERVICE</div>
          </div>
        </Link>
        <button
          onClick={() => setOpen(false)}
          className="inline-flex items-center justify-center w-11 h-11 text-white"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      <div className="px-5 pt-6 pb-10 flex flex-col gap-1">
        {NAV.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            onClick={() => setOpen(false)}
            data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`px-3 py-4 font-display text-3xl tracking-wide border-b border-[var(--ac-border)] ${
              location.pathname === n.to ? "text-[var(--ac-red)]" : "text-white"
            }`}
          >
            {n.label}
          </Link>
        ))}
        <div className="mt-6 flex flex-col gap-3">
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="btn-primary w-full justify-center"
            data-testid="mobile-quote-button"
          >
            Request a Quote
          </Link>
          <a
            href={`tel:${SITE.phoneE164}`}
            className="btn-ghost w-full justify-center"
            data-testid="mobile-call-button"
          >
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
        </div>
        <div className="mt-6 text-xs text-zinc-500 font-mono-data uppercase tracking-wider">
          {SITE.hours}<br />
          <span className="text-[var(--ac-red)]">{SITE.emergencyHours}</span>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-black/80 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-[64px] md:h-[72px] flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 md:gap-3 group min-w-0">
          <img
            src={SITE.logo}
            alt="Alliance Crane Service LLC logo"
            className="h-10 w-10 md:h-12 md:w-12 object-contain shrink-0"
          />
          <div className="leading-tight min-w-0">
            <div className="font-display text-base md:text-lg tracking-tight truncate">ALLIANCE</div>
            <div className="font-display text-[10px] md:text-[11px] text-[var(--ac-red)] tracking-[0.2em] truncate">
              CRANE SERVICE
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm uppercase tracking-[0.18em] font-semibold transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a
            href={`tel:${SITE.phoneE164}`}
            data-testid="header-call-button"
            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold tracking-wider text-white hover:text-[var(--ac-red)] transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span className="font-mono-data">{SITE.phone}</span>
          </a>

          <a
            href={`tel:${SITE.phoneE164}`}
            data-testid="header-call-mobile"
            aria-label={`Call ${SITE.phone}`}
            className="md:hidden inline-flex items-center justify-center w-11 h-11 border border-[var(--ac-border)] bg-black text-white hover:border-[var(--ac-red)] hover:text-[var(--ac-red)] transition-colors"
          >
            <Phone className="w-4 h-4" />
          </a>

          <Link
            to="/contact"
            data-testid="header-quote-button"
            className="hidden md:inline-flex btn-primary !py-2.5 !px-4 text-sm !min-h-0"
          >
            Request Quote
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-white"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {menu}
    </header>
  );
}
